import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { translations } from "../translations";

const Header = ({ lang, setLang, onLangClick, onReserveClick }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const t = translations[lang]?.nav || translations.fr.nav;
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [destinationsOpen, setDestinationsOpen] = useState(false);

  const handleLogoClick = () => {
    setMobileMenuOpen(false);
    navigate('/');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleNavigation = (path) => {
    setMobileMenuOpen(false);
    setDestinationsOpen(false);
    navigate(path);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  const handleReserveButton = () => {
    if (onReserveClick) {
      onReserveClick();
    } else {
      handleNavigation('/reserver');
    }
  };

  return (
    <header className="fixed w-full top-0 z-50 bg-black bg-opacity-95 backdrop-blur-sm shadow-lg">
      <nav className="container mx-auto px-4 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 cursor-pointer" onClick={handleLogoClick}>
            <h1 className="text-2xl font-bold text-yellow-500">BRUTAXI</h1>
            <span className="text-sm text-gray-300">Belgium</span>
          </div>

          {/* Mobile Menu Toggle */}
          <button
            className="md:hidden text-white p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            aria-label="Toggle menu"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              {mobileMenuOpen ? (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              ) : (
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
              )}
            </svg>
          </button>

          {/* Desktop Menu */}
          <div className="hidden md:flex items-center space-x-6">
            {/* Language Selector - Only show if onLangClick is provided */}
            {onLangClick && (
              <button onClick={onLangClick} className="flex items-center space-x-2 text-white hover:text-yellow-500 transition-colors">
                <span className="text-xl">
                  {lang === 'fr' && '🇫🇷'}
                  {lang === 'nl' && '🇳🇱'}
                  {lang === 'en' && '🇬🇧'}
                </span>
                <span className="text-sm">{lang?.toUpperCase() || 'FR'}</span>
              </button>
            )}
            
            <button 
              onClick={() => handleNavigation('/')} 
              className={`text-white hover:text-yellow-500 transition-colors ${location.pathname === '/' ? 'text-yellow-500' : ''}`}
            >
              {t.home}
            </button>

            {/* Destinations Dropdown */}
            <div 
              className="relative"
              onMouseEnter={() => setDestinationsOpen(true)}
              onMouseLeave={() => setDestinationsOpen(false)}
            >
              <button 
                onClick={() => handleNavigation('/destinations')}
                className={`text-white hover:text-yellow-500 transition-colors flex items-center ${location.pathname.startsWith('/destinations') || location.pathname === '/aeroports' ? 'text-yellow-500' : ''}`}
                data-testid="nav-destinations"
              >
                {t.destinations}
                <svg className="w-4 h-4 ml-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>

              {destinationsOpen && (
                <div className="absolute left-0 top-full pt-2 w-64 z-50">
                  <div className="bg-gray-800 rounded-lg shadow-xl py-2 border border-gray-700">
                    <button 
                      onClick={() => handleNavigation('/destinations')}
                      className="block w-full text-left px-4 py-2 text-white hover:bg-gray-700 hover:text-yellow-500 transition-colors"
                      data-testid="nav-destinations-all"
                    >
                      📍 Toutes les Destinations
                    </button>
                    <div className="border-t border-gray-700 my-2"></div>
                    <button 
                      onClick={() => handleNavigation('/aeroports')}
                      className="block w-full text-left px-4 py-2 text-white hover:bg-gray-700 hover:text-yellow-500 transition-colors"
                      data-testid="nav-destinations-airports"
                    >
                      ✈️ Aéroports
                    </button>
                  </div>
                </div>
              )}
            </div>

            <button 
              onClick={() => handleNavigation('/pourquoi-nous')} 
              className={`text-white hover:text-yellow-500 transition-colors ${location.pathname === '/pourquoi-nous' ? 'text-yellow-500' : ''}`}
            >
              {t.whyUs}
            </button>

            <button 
              onClick={() => handleNavigation('/contact')} 
              className={`text-white hover:text-yellow-500 transition-colors ${location.pathname === '/contact' ? 'text-yellow-500' : ''}`}
            >
              {t.contact}
            </button>

            <button
              onClick={handleReserveButton}
              className="bg-yellow-500 hover:bg-yellow-600 text-black px-6 py-2 rounded-lg font-semibold transition-all transform hover:scale-105"
              data-testid="nav-reserve"
            >
              {t.reserve}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden mt-4 pb-4 space-y-2 border-t border-gray-700 pt-4">
            {onLangClick && (
              <button onClick={onLangClick} className="w-full text-left flex items-center space-x-2 text-white hover:text-yellow-500 transition-colors py-2">
                <span className="text-xl">
                  {lang === 'fr' && '🇫🇷'}
                  {lang === 'nl' && '🇳🇱'}
                  {lang === 'en' && '🇬🇧'}
                </span>
                <span>{lang?.toUpperCase() || 'FR'}</span>
              </button>
            )}
            <button 
              onClick={() => handleNavigation('/')} 
              className="w-full text-left text-white hover:text-yellow-500 transition-colors py-2"
            >
              {t.home}
            </button>
            <button 
              onClick={() => handleNavigation('/destinations')} 
              className="w-full text-left text-white hover:text-yellow-500 transition-colors py-2"
            >
              {t.destinations}
            </button>
            <button 
              onClick={() => handleNavigation('/pourquoi-nous')} 
              className="w-full text-left text-white hover:text-yellow-500 transition-colors py-2"
            >
              {t.whyUs}
            </button>
            <button 
              onClick={() => handleNavigation('/contact')} 
              className="w-full text-left text-white hover:text-yellow-500 transition-colors py-2"
            >
              {t.contact}
            </button>
            <button
              onClick={handleReserveButton}
              className="w-full bg-yellow-500 hover:bg-yellow-600 text-black px-4 py-3 rounded-lg font-semibold transition-colors mt-4"
            >
              {t.reserve}
            </button>
          </div>
        )}
      </nav>
    </header>
  );
};

export default Header;